import type { MemberOut, TeamOut } from "@sagot/api-client";
import { useTranslation } from "@sagot/i18n";
import { Avatar, Button, EmptyState, ErrorState, ICONS, Icon, Skeleton, cn, type IconName } from "@sagot/ui";
import { RoutingNote } from "./routing-note";
import { teamTraits } from "./routing-model";

const TEAM_TONES = ["bg-primary-soft text-primary-ink", "bg-accent-soft text-accent-ink", "bg-success-soft text-success"] as const;
const AVATAR_STACK = 4;

function teamIcon(icon: string | null): IconName {
  return icon && icon in ICONS ? (icon as IconName) : "users";
}

interface TeamsCardProps {
  teams: readonly TeamOut[] | undefined;
  members: readonly MemberOut[];
  isError: boolean;
  onRetry: () => void;
  canManage: boolean;
  onCreate: () => void;
  onEdit: (team: TeamOut) => void;
}

function TeamRow({ team, index, members, canManage, onEdit }: { team: TeamOut; index: number; members: readonly MemberOut[]; canManage: boolean; onEdit: (team: TeamOut) => void }) {
  const { t } = useTranslation();
  const people = members.filter((m) => team.member_ids.includes(m.id));
  const traits = teamTraits(team, members);
  const details = [
    traits.roundRobin ? t("team.teams.traits.roundRobin") : t("team.teams.traits.queue"),
    ...(traits.lead ? [t("team.teams.traits.lead", { name: traits.lead })] : []),
    ...(traits.email ? [t("team.teams.traits.email", { email: traits.email })] : []),
  ];
  return (
    <li className="flex items-center gap-3 px-5 py-3">
      <span className={cn("inline-flex size-8 shrink-0 items-center justify-center rounded-md", TEAM_TONES[index % TEAM_TONES.length])}>
        <Icon name={teamIcon(team.icon)} className="size-4" />
      </span>
      <div className="min-w-0 flex-1">
        <div className="text-sm font-semibold">{team.name}</div>
        {team.description ? <div className="truncate text-xs text-fg-subtle">{team.description}</div> : null}
        <div className="truncate text-xs text-fg-muted" data-testid="team-traits">
          {details.join(" · ")}
        </div>
      </div>
      <div className="flex -space-x-1.5" aria-label={t("team.teams.membersOf", { name: team.name })} role="img">
        {people.length === 0 ? <span className="text-xs text-fg-subtle">{t("team.teams.noMembers")}</span> : null}
        {people.slice(0, AVATAR_STACK).map((m) => (
          <Avatar key={m.id} name={m.name} className={cn("size-7 text-[10px] ring-2 ring-surface", m.is_you && "bg-brand-500 text-ink-950 dark:bg-brand-500 dark:text-ink-950")} />
        ))}
      </div>
      {canManage ? (
        <Button variant="ghost" size="sm" iconOnly aria-label={t("team.teams.editTeam", { name: team.name })} onClick={() => { onEdit(team); }}>
          <Icon name="pencil" />
        </Button>
      ) : null}
    </li>
  );
}

/** Teams list (icon, name, description, avatar stack) + handoff routing note. */
export function TeamsCard({ teams, members, isError, onRetry, canManage, onCreate, onEdit }: TeamsCardProps) {
  const { t } = useTranslation();
  return (
    <section className="card" aria-labelledby="teams-title">
      <div className="card-header">
        <h2 id="teams-title" className="card-title">
          {t("team.teams.title")}
        </h2>
        {canManage ? (
          <Button variant="ghost" size="sm" onClick={onCreate}>
            <Icon name="plus" />
            {t("team.teams.newTeam")}
          </Button>
        ) : null}
      </div>
      {isError ? (
        <ErrorState headingLevel={3} title={t("team.teams.loadError")} onRetry={onRetry} retryLabel={t("common.retry")} />
      ) : !teams ? (
        <ul className="divide-y divide-line" aria-hidden="true">
          {Array.from({ length: 3 }, (_, i) => (
            <li key={i} className="flex items-center gap-3 px-5 py-3">
              <Skeleton className="size-8" />
              <Skeleton className="h-4 flex-1" />
            </li>
          ))}
        </ul>
      ) : teams.length === 0 ? (
        <EmptyState
          icon="users"
          headingLevel={3}
          title={t("team.teams.emptyTitle")}
          description={t("team.teams.emptyBody")}
          action={
            canManage ? (
              <Button variant="secondary" size="sm" onClick={onCreate}>
                <Icon name="plus" />
                {t("team.teams.newTeam")}
              </Button>
            ) : undefined
          }
        />
      ) : (
        <ul className="divide-y divide-line">
          {teams.map((team, index) => (
            <TeamRow key={team.id} team={team} index={index} members={members} canManage={canManage} onEdit={onEdit} />
          ))}
        </ul>
      )}
      {teams && teams.length > 0 ? <RoutingNote teams={teams} members={members} /> : null}
    </section>
  );
}
